import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { State, Store } from '@ngrx/store';
import { LocationService, MapsService, Sublocation } from '@uzurv/clara';
import { of } from 'rxjs';
import { catchError, debounceTime, map, switchMap, withLatestFrom } from 'rxjs/operators';
import { AlertDialogComponent } from '../../dialogs/alert-dialog/alert-dialog.component';
import * as fromLocationManager from './';
import { LocationManagerState } from './location-manager.reducer';

@Injectable()
export class LocationManagerEffects {
  getAutocompleteResults$ = createEffect(() => {
    return this.actions$.pipe(
      ofType<fromLocationManager.Search>(fromLocationManager.LocationManagerActionTypes.Search),
      switchMap(action =>
        this.mapsService.autocomplete(action.payload).pipe(
          map(autoCompleteResults => new fromLocationManager.SearchResultsLoaded(autoCompleteResults)),
          catchError(error => of(new fromLocationManager.LoadSearchResultsFailed(error)))
        )
      )
    );
  });

  getLocation$ = createEffect(() => {
    return this.actions$.pipe(
      ofType<fromLocationManager.SearchResultSelected>(fromLocationManager.LocationManagerActionTypes.SearchResultSelected),
      switchMap(action =>
        this.locationService.addLocation(action.payload).pipe(
          map(location => new fromLocationManager.LocationLoaded(location)),
          catchError(error => of(new fromLocationManager.LoadLocationFailed(error)))
        )
      )
    );
  });

  submitNewLocation$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(fromLocationManager.LocationManagerActionTypes.SubmitNewLocation),
      withLatestFrom(this.store$.select(fromLocationManager.locationManagerState)),
      switchMap(([_, state]) => {
        const sublocation = new Sublocation();
        sublocation.locationId = state.loadedLocation.id;
        sublocation.name = state.newLocationName;
        sublocation.latitude = state.mapCenterCoords.lat;
        sublocation.longitude = state.mapCenterCoords.lng;
        sublocation.description = null;
        return this.locationService.addSublocation(state.loadedLocation.id, sublocation).pipe(
          map(sub => new fromLocationManager.NewLocationAccepted(sub)),
          catchError(error => of(new fromLocationManager.SubmitNewLocationFailed(error)))
        );
      })
    );
  });

  deleteSublocation$ = createEffect(() => {
    return this.actions$.pipe(
      ofType<fromLocationManager.DeleteSublocation>(fromLocationManager.LocationManagerActionTypes.DeleteSublocation),
      switchMap(action =>
        this.locationService.deleteSublocation(action.payload.id).pipe(
          map(() => new fromLocationManager.DeleteSublocationSuccess()),
          catchError(error => of(new fromLocationManager.DeleteSublocationFailed(error)))
        )
      )
    );
  });

  getUserLocation$ = createEffect(() =>
    this.actions$.pipe(
      ofType<fromLocationManager.GetUserLocation>(fromLocationManager.LocationManagerActionTypes.GetUserLocation),
      switchMap(() =>
        this.mapsService.getUserLocation().pipe(
          map(position => new fromLocationManager.GetUserLocationSuccess(position)),
          catchError(error => of(new fromLocationManager.GetUserLocationFailed(error)))
        )
      )
    )
  );

  geocodeOffsetLocation$ = createEffect(() =>
    this.actions$.pipe(
      ofType<fromLocationManager.MoveMap>(fromLocationManager.LocationManagerActionTypes.MoveMap),
      debounceTime(500),
      switchMap(action =>
        this.mapsService.geocodeLocationByLatLng(action.payload.lat, action.payload.lng).pipe(
          map(location => new fromLocationManager.GeocodeOffsetLocationSuccess(location)),
          catchError(error => of(new fromLocationManager.GeocodeOffsetLocationFailed(error)))
        )
      )
    )
  );

  error$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType<
          | fromLocationManager.LoadSearchResultsFailed
          | fromLocationManager.LoadLocationFailed
          | fromLocationManager.NewLocationInvalid
          | fromLocationManager.SubmitNewLocationFailed
          | fromLocationManager.DeleteSublocationFailed
        >(
          fromLocationManager.LocationManagerActionTypes.LoadSearchResultsFailed,
          fromLocationManager.LocationManagerActionTypes.LoadLocationFailed,
          fromLocationManager.LocationManagerActionTypes.NewLocationInvalid,
          fromLocationManager.LocationManagerActionTypes.SubmitNewLocationFailed,
          fromLocationManager.LocationManagerActionTypes.DeleteSublocationFailed
        ),
        map(action => {
          if (action instanceof fromLocationManager.LoadLocationFailed && action.payload.message.includes('SQL')) {
            action.payload.message = `That doesn't seem to be a valid location; please try searching a more specific address`;
          }

          this.dialog.open(AlertDialogComponent, {
            data: {
              title: 'Error',
              message: action.payload.message
            }
          });
        })
      );
    },
    { dispatch: false }
  );

  loading$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(
          fromLocationManager.LocationManagerActionTypes.SearchResultSelected,
          fromLocationManager.LocationManagerActionTypes.SubmitNewLocation,
          fromLocationManager.LocationManagerActionTypes.DeleteSublocation
        )
      );
    },
    { dispatch: false }
  );

  doneLoading$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(
          fromLocationManager.LocationManagerActionTypes.LocationLoaded,
          fromLocationManager.LocationManagerActionTypes.LoadLocationFailed,
          fromLocationManager.LocationManagerActionTypes.NewLocationAccepted,
          fromLocationManager.LocationManagerActionTypes.SubmitNewLocationFailed,
          fromLocationManager.LocationManagerActionTypes.DeleteSublocationSuccess,
          fromLocationManager.LocationManagerActionTypes.DeleteSublocationFailed
        )
      );
    },
    { dispatch: false }
  );

  constructor(
    private actions$: Actions,
    private store$: Store<State<LocationManagerState>>,
    private mapsService: MapsService,
    private locationService: LocationService,
    private dialog: MatDialog
  ) {}
}
